import { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../ui/button';
import { Menu, X, Bell, ChevronDown, User, LogOut, Settings, LayoutDashboard } from 'lucide-react';
import { NotificationDropdown } from './NotificationDropdown';

const LOGO_URL = "/images/area-logo.png";

// Public nav links
const publicNavLinks = [];

export function Navbar() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, loading, logout, isAdmin } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [opportunitiesOpen, setOpportunitiesOpen] = useState(false);
  const [mobileOpportunitiesOpen, setMobileOpportunitiesOpen] = useState(false);
  const userMenuRef = useRef(null);
  const opportunitiesRef = useRef(null);

  const isAuthenticated = !!user;

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setUserMenuOpen(false);
      }
      if (opportunitiesRef.current && !opportunitiesRef.current.contains(event.target)) {
        setOpportunitiesOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const isActiveLink = (href) => {
    if (href === '/') return location.pathname === '/';
    return location.pathname === href || location.pathname.startsWith(href + '/');
  };

  const isOpportunitiesActive = () => {
    const opportunityPaths = ['/directory', '/referrals', '/jobs', '/discounts', '/affiliate'];
    return opportunityPaths.some(path => location.pathname.startsWith(path));
  };

  return (
    <header className="sticky top-0 z-50 bg-white border-b border-slate-200 shadow-sm" data-testid="navbar">
      <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
        <div className="flex items-center justify-between h-[88px]">
          {/* Logo */}
          <Link to="/" className="flex items-center" data-testid="logo">
            <img 
              src={LOGO_URL} 
              alt="American Real Estate Alliance" 
              className="h-[76px] w-auto"
            />
          </Link>

          {/* Desktop Navigation - Center Logo */}
          <nav className="hidden lg:flex items-center space-x-1">
            {isAuthenticated && location.pathname.startsWith('/split-report') && (
              <Link to="/split-report" data-testid="nav-split-report-logo">
                <img src="/images/split-report-logo.png" alt="Split Report" className="h-[60px] w-auto object-contain" />
              </Link>
            )}
            {isAuthenticated && !location.pathname.startsWith('/split-report') && (
              <Link
                to="/dashboard"
                className={`px-3 py-2 text-sm font-medium transition-colors rounded-md ${
                  location.pathname.startsWith('/dashboard')
                    ? 'text-[#3D4F7A] bg-slate-100'
                    : 'text-slate-600 hover:text-[#3D4F7A] hover:bg-slate-50'
                }`}
                data-testid="nav-dashboard"
              >
                Dashboard
              </Link>
            )}}
            {publicNavLinks.map((link) => {
              if (link.dropdown) {
                return (
                  <div key={link.name} className="relative" ref={opportunitiesRef}>
                    <button
                      onClick={() => setOpportunitiesOpen(!opportunitiesOpen)}
                      className={`flex items-center gap-1 px-3 py-2 text-sm font-medium transition-colors rounded-md ${
                        isOpportunitiesActive()
                          ? 'text-[#3D4F7A] bg-slate-100'
                          : 'text-slate-600 hover:text-[#3D4F7A] hover:bg-slate-50'
                      }`}
                      data-testid="nav-opportunities"
                    >
                      {link.name}
                      <ChevronDown className={`w-4 h-4 transition-transform ${opportunitiesOpen ? 'rotate-180' : ''}`} />
                    </button>
                    {opportunitiesOpen && (
                      <div className="absolute top-full left-0 mt-1 w-48 bg-white rounded-lg shadow-lg border border-slate-200 py-2 z-50">
                        {link.dropdown.map((item) => (
                          <Link
                            key={item.name}
                            to={item.href}
                            className={`block px-4 py-2 text-sm ${
                              isActiveLink(item.href)
                                ? 'text-[#3D4F7A] bg-slate-50 font-medium'
                                : 'text-slate-600 hover:text-[#3D4F7A] hover:bg-slate-50'
                            }`}
                            onClick={() => setOpportunitiesOpen(false)}
                          >
                            {item.name}
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                );
              }
              if (link.external) {
                return (
                  <a
                    key={link.name}
                    href={link.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`px-3 py-2 text-sm font-medium transition-colors rounded-md ${
                      link.highlighted
                        ? 'text-white bg-[#2563eb] hover:bg-[#1d4ed8] shadow-sm'
                        : 'text-slate-600 hover:text-[#3D4F7A] hover:bg-slate-50'
                    }`}
                    data-testid={`nav-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    {link.name}
                  </a>
                );
              }
              return (
                <Link
                  key={link.name}
                  to={link.href}
                  className={`px-3 py-2 text-sm font-medium transition-colors rounded-md ${
                    link.highlighted
                      ? 'text-white bg-[#2563eb] hover:bg-[#1d4ed8] shadow-sm'
                      : isActiveLink(link.href)
                        ? 'text-[#3D4F7A] bg-slate-100'
                        : 'text-slate-600 hover:text-[#3D4F7A] hover:bg-slate-50'
                  }`}
                  data-testid={`nav-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  {link.name}
                </Link>
              );
            })}
          </nav>

          {/* Right Side Actions */}
          <div className="hidden lg:flex items-center space-x-3">
            {isAuthenticated ? (
              <>
                {/* Notifications Dropdown */}
                <NotificationDropdown />

                {/* User Menu */}
                <div className="relative" ref={userMenuRef}>
                  <button
                    onClick={() => setUserMenuOpen(!userMenuOpen)}
                    className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-slate-600 hover:text-[#3D4F7A] hover:bg-slate-50 rounded-md transition-colors"
                    data-testid="user-menu-btn"
                  >
                    <div className="w-8 h-8 rounded-full bg-[#3D4F7A] text-white flex items-center justify-center text-sm font-semibold">
                      {user?.name?.charAt(0)?.toUpperCase() || 'U'}
                    </div>
                    <ChevronDown className={`w-4 h-4 transition-transform ${userMenuOpen ? 'rotate-180' : ''}`} />
                  </button>
                  {userMenuOpen && (
                    <div className="absolute top-full right-0 mt-1 w-56 bg-white rounded-lg shadow-lg border border-slate-200 py-2 z-50">
                      <div className="px-4 py-2 border-b border-slate-100">
                        <p className="font-medium text-slate-900 truncate">{user?.name}</p>
                        <p className="text-xs text-slate-500 truncate">{user?.email}</p>
                      </div>
                      <Link
                        to="/dashboard"
                        className="flex items-center gap-2 px-4 py-2 text-sm text-slate-600 hover:text-[#3D4F7A] hover:bg-slate-50"
                        onClick={() => setUserMenuOpen(false)}
                      >
                        <LayoutDashboard className="w-4 h-4" />
                        Dashboard
                      </Link>
                      <Link
                        to="/dashboard/profile"
                        className="flex items-center gap-2 px-4 py-2 text-sm text-slate-600 hover:text-[#3D4F7A] hover:bg-slate-50"
                        onClick={() => setUserMenuOpen(false)}
                      >
                        <User className="w-4 h-4" />
                        Profile
                      </Link>
                      {isAdmin && (
                        <Link
                          to="/admin"
                          className="flex items-center gap-2 px-4 py-2 text-sm text-slate-600 hover:text-[#3D4F7A] hover:bg-slate-50"
                          onClick={() => setUserMenuOpen(false)}
                        >
                          <Settings className="w-4 h-4" />
                          Admin Panel
                        </Link>
                      )}
                      <button
                        onClick={() => { handleLogout(); setUserMenuOpen(false); }}
                        className="flex items-center gap-2 w-full px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                      >
                        <LogOut className="w-4 h-4" />
                        Sign Out
                      </button>
                    </div>
                  )}
                </div>
              </>
            ) : (
              <>
                <Link to="/login">
                  <Button variant="ghost" className="text-slate-600 hover:text-[#3D4F7A]">
                    Log In
                  </Button>
                </Link>
                <Link to="/register">
                  <Button className="bg-[#3D4F7A] hover:bg-[#2A3755] text-white font-semibold">
                    Join AREA
                  </Button>
                </Link>
              </>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden p-2 text-slate-600 hover:text-[#3D4F7A] hover:bg-slate-100 rounded-md"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-btn"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden py-4 border-t" data-testid="mobile-menu">
            <div className="space-y-1">
              {isAuthenticated && (
                <Link
                  to="/dashboard"
                  onClick={() => setMobileMenuOpen(false)}
                  className={`block px-4 py-2 text-base font-medium rounded-md ${
                    location.pathname.startsWith('/dashboard')
                      ? 'text-[#3D4F7A] bg-slate-100'
                      : 'text-slate-600 hover:text-[#3D4F7A] hover:bg-slate-50'
                  }`}
                  data-testid="mobile-nav-dashboard"
                >
                  Dashboard
                </Link>
              )}
              {publicNavLinks.map((link) => {
                if (link.dropdown) {
                  return (
                    <div key={link.name}>
                      <button
                        onClick={() => setMobileOpportunitiesOpen(!mobileOpportunitiesOpen)}
                        className={`flex items-center justify-between w-full px-4 py-2 text-base font-medium rounded-md ${
                          isOpportunitiesActive()
                            ? 'text-[#3D4F7A] bg-slate-100'
                            : 'text-slate-600 hover:text-[#3D4F7A] hover:bg-slate-50'
                        }`}
                      >
                        {link.name}
                        <ChevronDown className={`w-4 h-4 transition-transform ${mobileOpportunitiesOpen ? 'rotate-180' : ''}`} />
                      </button>
                      {mobileOpportunitiesOpen && (
                        <div className="pl-4 space-y-1">
                          {link.dropdown.map((item) => (
                            <Link
                              key={item.name}
                              to={item.href}
                              className={`block px-4 py-2 text-sm ${
                                isActiveLink(item.href)
                                  ? 'text-[#3D4F7A] bg-slate-50 font-medium'
                                  : 'text-slate-500 hover:text-[#3D4F7A] hover:bg-slate-50'
                              }`}
                              onClick={() => setMobileMenuOpen(false)}
                            >
                              {item.name}
                            </Link>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                }
                if (link.external) {
                  return (
                    <a
                      key={link.name}
                      href={link.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`block px-4 py-2 text-base font-medium rounded-md ${
                        link.highlighted
                          ? 'text-white bg-[#2563eb] hover:bg-[#1d4ed8] mx-2 text-center'
                          : 'text-slate-600 hover:text-[#3D4F7A] hover:bg-slate-50'
                      }`}
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      {link.name}
                    </a>
                  );
                }
                return (
                  <Link
                    key={link.name}
                    to={link.href}
                    className={`block px-4 py-2 text-base font-medium rounded-md ${
                      link.highlighted
                        ? 'text-white bg-[#2563eb] hover:bg-[#1d4ed8] mx-2 text-center'
                        : isActiveLink(link.href)
                          ? 'text-[#3D4F7A] bg-slate-100'
                          : 'text-slate-600 hover:text-[#3D4F7A] hover:bg-slate-50'
                    }`}
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    {link.name}
                  </Link>
                );
              })}
            </div>

            {/* Mobile Auth Actions */}
            <div className="mt-4 pt-4 border-t space-y-2">
              {isAuthenticated ? (
                <>
                  <Link
                    to="/dashboard"
                    className="block px-4 py-2 text-base font-medium text-slate-600 hover:text-[#3D4F7A] hover:bg-slate-50 rounded-md"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Dashboard
                  </Link>
                  {isAdmin && (
                    <Link
                      to="/admin"
                      className="block px-4 py-2 text-base font-medium text-slate-600 hover:text-[#3D4F7A] hover:bg-slate-50 rounded-md"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      Admin Panel
                    </Link>
                  )}
                  <button
                    onClick={() => { handleLogout(); setMobileMenuOpen(false); }}
                    className="block w-full text-left px-4 py-2 text-base font-medium text-red-600 hover:bg-red-50 rounded-md"
                  >
                    Sign Out
                  </button>
                </>
              ) : (
                <>
                  <Link
                    to="/login"
                    className="block px-4 py-2 text-base font-medium text-slate-600 hover:text-[#3D4F7A] hover:bg-slate-50 rounded-md"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Log In
                  </Link>
                  <Link
                    to="/register"
                    className="block mx-4 px-4 py-2 text-base font-bold text-center text-white bg-[#3D4F7A] hover:bg-[#2A3755] rounded-md"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Join AREA
                  </Link>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </header>
  );
}
